import type {
  ConversionFactorUnit,
  QuantityUnit,
} from '@shared/models/unit.model';
import type { ElementCategoryID } from '@shared/models/element-categories.model';
import {
  elementQuantityUnitRecord,
  type ElementQuantityExpressionName,
} from '@shared/models/element-quantities.model';
import { getCorrespondingConversionFactorUnit } from '@shared/helpers/conversion.helpers';
import { isElementQuantityExpressionName } from '@shared/helpers/element-quantity.helpers';
import type { CandidateRow } from '@shared/models/element-import.model';
import { resolveRecipeQuantityName } from '@shared/helpers/materialize-candidate.helpers';

/**
 * Round a conversion rate for display: large magnitudes to a whole number,
 * otherwise to 6 significant figures. Shared by the seeded value and the
 * swapped (inverse) value so both orientations format consistently.
 */
export const formatCandidateConversionRate = (n: number): number =>
  Math.abs(n) >= 100000 ? Math.round(n) : parseFloat(n.toPrecision(6));

/**
 * The unit the conversion rate is expressed in ("<unit> per <CSV unit>").
 *
 * Priority:
 * 1. Persisted `unitTo` — an explicit pick or calibrated entry always wins.
 * 2. {@link amountUnit} — the unit shown in the Amount column (picked element
 *    quantity / product default, e.g. m² on a door), so the header rate follows
 *    Amount instead of the product's native unit (kg).
 * 3. Recipe resolution via {@link resolveRecipeQuantityName} when no Amount unit
 *    is known yet.
 * 4. Product native `unit` as last resort.
 *
 * Returns `undefined` when the target is not known yet. Callers must render
 * nothing rather than guess: claiming a unit the target cannot express is what
 * "kg per kg" on a wall was (DEV-3456).
 */
export const resolveConversionDisplayUnit = (params: {
  row: CandidateRow;
  savedUnitTo: ConversionFactorUnit | undefined;
  /**
   * Unit currently shown in the Amount column (element quantity unit). Prefer
   * this over {@link productUnit} so omräkningsfaktor tracks Amount.
   */
  amountUnit: QuantityUnit | undefined;
  isRecipe: boolean;
  /** The mapping's persisted quantity pick, when there is one. */
  savedQuantityName: ElementQuantityExpressionName | undefined;
  /** The materialized element's selected quantity, when the row has one. */
  elementQuantityName: string | undefined;
  /** The mapped recipe's category, for a row that has not materialized yet. */
  recipeCategoryId: ElementCategoryID | undefined;
  productUnit: string | undefined;
}): ConversionFactorUnit | undefined => {
  const {
    row,
    savedUnitTo,
    amountUnit,
    isRecipe,
    savedQuantityName,
    elementQuantityName,
    recipeCategoryId,
    productUnit,
  } = params;
  if (savedUnitTo) return savedUnitTo;
  // Not every quantity unit is a conversion-factor unit (`kg/m²`, `%`, `hr`):
  // the rate is persisted as `unitTo`, so an unconvertible Amount unit must
  // fall through rather than be claimed (DEV-3456).
  const amountConversionUnit = amountUnit
    ? getCorrespondingConversionFactorUnit(amountUnit, false)
    : undefined;
  if (amountConversionUnit) return amountConversionUnit;

  if (!isRecipe) return productUnit as ConversionFactorUnit | undefined;

  const quantityName = resolveRecipeQuantityName({
    row,
    categoryId: recipeCategoryId,
    explicitQuantity:
      savedQuantityName ??
      (elementQuantityName &&
      isElementQuantityExpressionName(elementQuantityName)
        ? elementQuantityName
        : undefined),
  });

  return quantityName
    ? (elementQuantityUnitRecord[quantityName] as
        ConversionFactorUnit | undefined)
    : undefined;
};
